// Purpose: /dropboxes/new/ephemeral — single-use dropbox creation form.
// Phase 7.5 port: PageHeader + Card + Input/Select + Toggle + Button.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEphemeralDropbox, type CreateEphemeralResponse } from '../api/dropbox.ts';
import { PageHeader } from '../components/nav/PageHeader.tsx';
import { RoutingPicker, type RoutingPickerValue } from '../components/RoutingPicker.tsx';
import { Button, Card, Input, Select, Toggle } from '../components/ui/index.ts';

export function NewEphemeralDropbox() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [expiresInHours, setExpiresInHours] = useState<4 | 24 | 168>(24);
  const [deleteOnRetrieve, setDeleteOnRetrieve] = useState(true);
  const [routing, setRouting] = useState<RoutingPickerValue>({
    config: undefined,
    incomplete: false,
    folderPathWarning: null,
  });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<CreateEphemeralResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (label.trim().length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const res = await createEphemeralDropbox({
        label: label.trim(),
        expiresInHours,
        deleteOnRetrieve,
        ...(routing.config ? { routing: routing.config } : {}),
      });
      setResult(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  if (result) {
    return (
      <div className="flex flex-col gap-6 max-w-2xl">
        <PageHeader
          title="Dropbox ready"
          subtitle="Send this link to whoever should upload files. Anyone with the link can upload until the link expires or you revoke it."
        />
        <Card padding="compact">
          <div className="text-label uppercase text-fg-secondary mb-2">Link</div>
          <div className="flex items-center gap-2">
            <code className="text-body-s text-fg-primary break-all">{result.linkUrl}</code>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(result.linkUrl)}
            >
              Copy
            </Button>
          </div>
          {result.expiresAt ? (
            <div className="text-body-s text-fg-muted mt-3">
              Expires {new Date(result.expiresAt).toLocaleString()}
            </div>
          ) : null}
        </Card>
        <div>
          <Button variant="secondary" onClick={() => navigate(`/dropboxes/${result.dropboxId}`)}>
            Open dropbox
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <PageHeader title="New ephemeral dropbox" />
      <Card padding="comfortable">
        <div className="flex flex-col gap-5">
          <Input
            label="Label (visible to uploader)"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Resume submission - Jane Doe"
            maxLength={200}
          />
          <Select
            label="Expires in"
            value={expiresInHours}
            onChange={(e) => setExpiresInHours(Number(e.target.value) as 4 | 24 | 168)}
          >
            <option value={4}>4 hours</option>
            <option value={24}>24 hours</option>
            <option value={168}>7 days</option>
          </Select>
          <Toggle
            checked={deleteOnRetrieve}
            onChange={setDeleteOnRetrieve}
            label="Delete on retrieve"
            description="Delete each file from storage after I download it. Leave on for sensitive material."
          />

          <RoutingPicker value={routing} onChange={setRouting} onError={setError} />

          {error ? (
            <div
              role="alert"
              className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
            >
              {error}
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/dropboxes')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={creating}
              disabled={
                label.trim().length === 0 || routing.incomplete || !!routing.folderPathWarning
              }
              onClick={() => void handleCreate()}
            >
              Create dropbox
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
